<template>
  <v-container>
    <v-row class="mb-4 justify-space-between">
      <v-col align-items-center>
        <h1 class="text text-h4 titulo">
          Documentos Recibidos
        </h1>
      </v-col>
      <v-col class="text-end d-flex justify-end">
        <Can I="read" a="PurchaseInvoice">
          <v-btn 
            class="ml-4" 
            text
            color="secondary"
            :loading="exporting"
            @click="exportToExcel"
          >
            <v-icon class="mr-2">fas fa-file-excel</v-icon>Exportar
          </v-btn>
        </Can>
        <Can I="create" a="PurchaseInvoice">
          <v-btn class="ml-4" color="success" @click="$router.push({name:'CreatePurchaseInvoice',params:{id:'new'}})">
            <v-icon class="mr-2">fas fa-plus</v-icon>Nueva
          </v-btn>
        </Can>
      </v-col>
    </v-row>
    
    <v-card class='pa-4'>
      <v-row>
        <v-col cols="12" md="6" align-self="center">
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.received_after' 
            class="mr-4" 
            outlined 
            clearable 
            type="date" 
            label="Creada Desde:">
          </v-text-field>
        </v-col>
        <v-col cols="12" md="6" align-self="center">
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.received_before' 
            clearable 
            outlined 
            type="date" 
            label="Creada Hasta:">
          </v-text-field>
        </v-col>
        <v-col>
          <v-select
            v-model="filter.estatus"
            :items="estatuses"
            item-text="text"
            item-value="value"
            label="Estatus"
            placeholder="Todos los Estatus"
            :disabled="loading"
            single-line
            outlined
            dense
            clearable
            prepend-inner-icon="filter_alt"
          />
        </v-col>
        <v-col>
          <v-select
            v-model="filter.tipo"
            :items="tipos"
            item-text="text"
            item-value="value"
            label="Tipo"
            placeholder="Todos los Tipos"
            single-line
            :disabled="loading"
            outlined
            dense
            clearable
            prepend-inner-icon="filter_alt"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="search"
            @keyup="searchTimeOut"
            label="Buscar"
            append-icon="fas fa-search"
            :disabled="loading"
          ></v-text-field>
        </v-col>
      </v-row>
      
      <v-row>
        <v-col>
          <v-data-table
            class=""
            :headers="computedHeaders"
            :items="invoices"
            :items-per-page="10"
            :loading="loading"
            :options.sync="options"
            :server-items-length="parseInt(total)"
            :item-class="()=>{return 'row-style-normal'}"
            :search="search"
            sort-by='created_at'
            :sort-desc="false"
            :footer-props="{
              showFirstLastPage: true,
              firstIcon: 'fas fa-angle-double-left',
              lastIcon: 'fas fa-angle-double-right',
              prevIcon: 'fas fa-angle-left',
              nextIcon: 'fas fa-angle-right',
              itemsPerPageOptions: [10, 20, 30, 40, 50,100],
            }">
            
            <template v-slot:item.document_id="{ item }">
              <div class="d-flex flex-column">
                <div class="text">{{ item.document_id }}</div>
                <v-chip
                  v-if="document_types.find(e=>e.value==item.tipo).color" 
                  x-small
                  :color="document_types.find(e=>e.value==item.tipo).color"
                  class=""
                  >
                  <span class="text">{{ document_types.find(e=>e.value==item.tipo).text }}</span>
                </v-chip>
              </div>
            </template>
            <template v-slot:item.purchase_order="{ item }">
              <div class="d-flex flex-column">
                <div class="text text-end">{{ item.purchase_order }}</div>
                <v-chip
                  v-if="item.work_order"
                  x-small
                  color="grey"
                  class="text-end mx-auto"
                  >
                  <span class="mx-auto">OT: {{ item.work_order }}</span>
                </v-chip>
              </div>
            </template>
            
            <template v-slot:item.date_received="{ item }">
              <div class="text">{{ item.date_received | moment("DD/MM/YYYY") }}</div>
            </template>
            
            <template v-slot:item.get_total="{ item }">
              <div class="text text-end">
                {{ item.get_total | moneda }}
              </div>
            </template>
            
            <template v-slot:item.total_payed="{ item }">
              <div class="text text-end">
                {{ item.get_payed | moneda }}
              </div>
            </template>
            
            <template v-slot:item.estatus="{ item }">
              <v-chip small label outlined :color="getStatusColor(item)" style="">
                {{ getStatusName(item) }}
              </v-chip>
            </template>
            
            <template v-slot:item.actions="{ item }">
              <v-menu
                bottom left offset-y
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon 
                    v-bind="attrs"
                    v-on="on"
                    text
                    class="ml-2">more_vert</v-icon> 
                </template>
                <v-list dense>
                  <Can I="update" a="PurchaseInvoice">
                    <v-list-item>
                      <v-btn 
                        text 
                        small
                        @click.stop="editInvoice(item)" 
                      >
                        <v-icon class="mr-2">fas fa-edit</v-icon>Editar
                      </v-btn>
                    </v-list-item>
                  </Can>
                  <Can I="update" a="PurchaseInvoice">
                    <v-list-item>
                      <v-btn 
                        text 
                        small
                        @click.stop="showPayments(item)" 
                      >
                        <v-icon class="mr-2">fas fa-money-check-alt</v-icon>Pagos
                      </v-btn>
                    </v-list-item>
                  </Can>
                  <Can I="update" a="PurchaseInvoice">
                    <v-list-item v-if="item.estatus !== 3">
                      <v-btn 
                        text 
                        small
                        @click.stop="cancelInvoice(item)" 
                        class="error--text"
                      >
                        <v-icon class="mr-2">fas fa-ban</v-icon>Anular
                      </v-btn>
                    </v-list-item>
                  </Can>
                  <Can I="delete" a="PurchaseInvoice">
                    <v-list-item>
                      <v-btn 
                        text 
                        small 
                        @click.stop="deleteInvoice(item)" 
                        class="error--text">
                        <v-icon class="mr-2">fas fa-trash</v-icon>Eliminar
                      </v-btn>
                    </v-list-item> 
                  </Can>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card>
    
    <!-- Dialog para mostrar detalles -->
    <v-dialog v-model="showInvoiceDetails" max-width="1200">
      <v-card class="pa-6">
        <detailed-purchase-invoice 
          :purchaseInvoice="invoiceToDisplay" 
          @close="showInvoiceDetails=false"
        />
      </v-card>
    </v-dialog>
    
    <!-- Dialog para mostrar pagos -->
    <v-dialog v-model="showPaymentsDialog" max-width="1200">
      <v-card class="pa-6">
        <v-card-title class="titulo text-h5">Historial de Pagos</v-card-title>
        <purchase-invoice-pay-detail 
          :purchaseInvoice="invoiceToPay" 
          @refresh="()=>{
            showPaymentsDialog=false
            fetch()
          }"
        />
      </v-card>
    </v-dialog>
    
    <!-- Confirmación para eliminar factura -->
    <confirm
      v-if="invoiceToDelete"
      v-model="showConfirmDelete" 
      :message="`¿Confirma la eliminación de la Factura #${invoiceToDelete.document_id}?`" 
      @ok="confirmDeleteInvoice"
      @cancel="showConfirmDelete=false"/>
      
    <!-- Confirmación para anular factura -->
    <confirm
      v-if="invoiceToCancel"
      v-model="showConfirmCancel" 
      :message="`¿Confirma la anulación de la Factura #${invoiceToCancel.document_id}?`" 
      @ok="confirmCancelInvoice"
      @cancel="showConfirmCancel=false"/>
  </v-container>
</template>

<script>
import { fetchPurchaseInvoices, deletePurchaseInvoice, updatePurchaseInvoice, exportPurchaseInvoicesToExcel } from '@/api/purchases'
import notifications from '@/utils/notifications'
import moment from 'moment'
import PurchaseInvoicePayDetail from '@/components/purchases/PurchaseInvoicePayDetail.vue'

export default {
  name: 'PurchaseInvoices',
  components: {
    PurchaseInvoicePayDetail,
    // DetailedPurchaseInvoice: () => import('@/components/purchases/DetailedPurchaseInvoice')
  },
  data() {
    return {
      loading: false,
      loadingInvoice: false,
      exporting: false,
      showInvoiceDetails: false,
      invoiceToDisplay: '',
      showPaymentsDialog: false,
      invoiceToPay: '',
      showConfirmDelete: false,
      invoiceToDelete: '',
      showConfirmCancel: false,
      invoiceToCancel: '',
      invoices: [],
      timer: '',
      options: {},
      search: '',
      total: '',
      filter: {
        received_after: moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('YYYY-MM-DD'),
        received_before: '',
        estatus: '',
        tipo: '',
      },
      document_types: [
        {
          value: 30,
          text: 'Factura',
          color: 'info'
        },
        {
          value: 50,
          text: 'Guia de Despacho',
          color: 'primary'
        },
        {
          value: 60,
          text: 'Nota de Credito',
          color: 'error'
        }
      ],
      headers: [
        {
          text: 'Número',
          align: 'start',
          value: 'document_id',
          class: 'table-header2',
          show: true,
          align: 'end'
        },
        {
          text: 'Fecha',
          align: 'start',
          value: 'date_received',
          class: 'table-header2',
          show: true
        },
        {
          text: 'Razón Social',
          align: 'start',
          value: 'rznSocEmisor',
          class: 'table-header2',
          show: true
        },
        {
          text: 'OC / OT',
          align: 'end',
          value: 'purchase_order',
          class: 'table-header2',
          show: true
        },
        {
          text: 'Total',
          align: 'end',
          value: 'get_total',
          class: 'table-header2',
          show: true,
        },
        {
          text: 'Pagado',
          align: 'end',
          value: 'total_payed',
          class: 'table-header2',
          show: true,
          sortable: false
        },
        {
          text: 'Estatus',
          align: 'center',
          value: 'estatus',
          class: 'table-header2',
          show: true
        },
        {
          text: '',
          align: 'center',
          value: 'actions',
          class: 'table-header',
          show: true,
          sortable: false
        }
      ],
      
      estatuses: [
        {
          value: 1,
          text: 'Recibida',
          color: 'info'
        },
        {
          value: 2,
          text: 'Pagada',
          color: 'success'
        },
        {
          value: 3,
          text: 'Anulada',
          color: 'error'
        }
      ],
      
      tipos: [
        {
          value: 30,
          text: 'Factura'
        },
        {
          value: 50,
          text: 'Guia de Despacho'
        },
        {
          value: 60,
          text: 'Nota de Credito'
        }
      ]
    }
  },
  
  computed: {
    computedHeaders() {
      return this.headers.filter(header => header.show)
    }
  },
  
  methods: {
    getStatusName(item) {
      return this.estatuses.find(i => i.value == item.estatus)?.text || 'Desconocido'
    },
    
    getStatusColor(item) {
      return this.estatuses.find(i => i.value == item.estatus)?.color || 'grey'
    },
    
    displayDetails(item) {
      this.invoiceToDisplay = item
      this.showInvoiceDetails = true
    },
    
    editInvoice(item) {
      this.$router.push({ name: 'CreatePurchaseInvoice', params: { id: item.id } })
    },
    
    showPayments(item) {
      this.invoiceToPay = item
      this.showPaymentsDialog = true
    },
    
    cancelInvoice(item) {
      this.invoiceToCancel = item
      this.showConfirmCancel = true
    },
    
    deleteInvoice(item) {
      this.invoiceToDelete = item
      this.showConfirmDelete = true
    },
    
    async confirmDeleteInvoice() {
      try {
        await deletePurchaseInvoice(this.invoiceToDelete.id)
        this.showConfirmDelete = false
        this.invoiceToDelete = ''
        notifications.showSuccess('Factura eliminada correctamente')
        this.fetch()
      } catch (error) {
        console.error(error)
        notifications.showError('Error eliminando factura: ' + error.message)
      }
    },

    async confirmCancelInvoice() {
      try {
        await updatePurchaseInvoice(this.invoiceToCancel.id, { estatus: 3 })
        this.showConfirmCancel = false
        this.invoiceToCancel = ''
        notifications.showSuccess('Factura anulada correctamente')
        this.fetch()
      } catch (error) {
        console.error(error)
        notifications.showError('Error anulando factura: ' + error.message)
      }
    },
    
    async fetch() {
      this.loading = true
      
      // Arreglando variables de fecha en caso de ser null
      if (!this.filter.received_after) this.filter.received_after = ''
      if (!this.filter.received_before) this.filter.received_before = ''
      
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      let sort = ''
      
      if (sortBy[0] == 'provider_name') {
        sort = 'provider__name'
      } else {
        sort = sortBy[0]
      }
      
      if (!this.filter.estatus) this.filter.estatus = ''
      if (!this.filter.tipo) this.filter.tipo = ''
      
      const query = `ordering=${sortDesc[0] ? sort : '-' + sort}&&page=${page}&&page_size=${itemsPerPage}&&search=${this.search}&&estatus=${this.filter.estatus}&&tipo=${this.filter.tipo}&&received_after=${this.filter.received_after}&received_before=${this.filter.received_before}`
      
      await fetchPurchaseInvoices(query)
        .then(res => {
          this.total = res.count
          this.invoices = res.results
        })
        .catch((err) => {
          console.log(err)
          notifications.showError('Error cargando facturas: ' + err.error)
        })
      
      this.loading = false
    },
    
    searchTimeOut() {
      // Función para hacer búsqueda lazy
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
      this.timer = setTimeout(() => {
        this.fetch()
      }, 800)
    },
    
    async exportToExcel() {
      this.exporting = true
      
      try {
        // Construir query con los mismos filtros del listado
        if (!this.filter.received_after) this.filter.received_after = ''
        if (!this.filter.received_before) this.filter.received_before = ''
        
        const { sortBy, sortDesc } = this.options
        let sort = ''
        
        if (sortBy[0] == 'provider_name') {
          sort = 'provider__name'
        } else {
          sort = sortBy[0]
        }
        
        if (!this.filter.estatus) this.filter.estatus = ''
        if (!this.filter.tipo) this.filter.tipo = ''
        
        const query = `ordering=${sortDesc[0] ? sort : '-' + sort}&&search=${this.search}&&estatus=${this.filter.estatus}&&tipo=${this.filter.tipo}&&received_after=${this.filter.received_after}&received_before=${this.filter.received_before}`
        
        const blob = await exportPurchaseInvoicesToExcel(query)
        
        // Crear URL del blob y descargar
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `facturas_compras_${new Date().toISOString().slice(0,10)}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        notifications.showSuccess('Archivo exportado correctamente')
      } catch (error) {
        console.error(error)
        notifications.showError('Error exportando archivo: ' + error.message)
      } finally {
        this.exporting = false
      }
    }
  },
  
  mounted() {
    this.fetch()
  },
  
  watch: {
    filter: {
      handler() {
        this.fetch()
      },
      deep: true
    },
    options:{
      handler(){
        this.fetch()
      },
      deep:true
    },
  }
}
</script> 